/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description paymentAllocation
 */
import _ from 'lodash';
import {
	institutionalIdentifierKeyAndCodeMapping,
	payerResponsibilitySequenceMapping,
	professionalIdentifierKeyAndCodeMapping,
	CLAIM_TYPES,
	CLAIM_WORK_STATES,
} from 'app-constants/claims/claimRequest';
import { findValueByIdentifierCode, formatDate } from 'utils/claims/institutionalClaimRequestParser';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { getPaymentMethod } from 'utils/payment/payments';
import { getChangedClaims } from 'utils/payment/paymentAllocationDerivation';
import { getIdentifierValue } from 'utils/parseFhirDataType';
import { getClaimResponseFinancialMeta } from 'utils/claims/claimInformation';

const calculatePendingPR = ({
	patientResponsibility,
	collectedPR,
	writeOff,
}: {
	patientResponsibility: number;
	collectedPR: { total: number; details: AnyType[] };
	writeOff: { amount: number; reasonId?: string | null; reason?: string | null };
}): number => {
	return (patientResponsibility || 0) - (collectedPR?.total || 0) - (writeOff?.amount || 0);
};

const getClaimFinancialMeta = (claimDetails: ClaimStatusResponse.IClaimPatientViewDetails): Partial<PatientPayment.IClaimInfo> => {
	const claimResponse = claimDetails?.fhirClaimResponse as Fhir.ClaimResponse;
	const { patientResponsibilityAmount, billedAmount, paidAmount, payerAdjusted } = getClaimResponseFinancialMeta(claimResponse);
	const writeOffDetails = claimDetails?.writeOffDetails?.[0];
	const writeOffAmount = writeOffDetails?.amount;
	const writeOffReasonId = writeOffDetails?.writeOffReasonId;

	const pendingPR = claimDetails?.pendingPR || 0;
	return {
		patientResponsibility: patientResponsibilityAmount,
		billedAmount: billedAmount || 0,
		writeOff: { amount: writeOffAmount, reasonId: writeOffReasonId },
		payerResponsibility: paidAmount,
		payerAdjusted,
		pendingPR,
	};
};

const getClaimInfo = (
	claimRequest: Fhir.Claim,
	isInstitutionalClaim: boolean,
	metaData: ClaimStatusResponse.IClaimPatientMetaData,
): Partial<PatientPayment.IClaimInfo> => {
	const claimMeta = {} as Partial<PatientPayment.IClaimInfo>;
	claimMeta.status = claimRequest?.status;

	const claimNumberConfig = isInstitutionalClaim
		? institutionalIdentifierKeyAndCodeMapping.claim.claimNumber
		: professionalIdentifierKeyAndCodeMapping.claim.claimNumber;

	claimMeta.claimNumber = findValueByIdentifierCode(
		claimRequest?.identifier,
		claimNumberConfig[0]?.code,
		claimNumberConfig[0]?.codeSystem,
		claimNumberConfig[0]?.valueSystem,
	)?.value;

	claimMeta.patientControlNumber = _.find(claimRequest?.identifier, (identifier) =>
		_.includes(identifier?.system, fhirSystemMapping.patientControlNumber),
	)?.value;

	const insurance = _.first(_.orderBy(claimRequest?.insurance, (item) => item?.focal, ['desc']));

	claimMeta.patientResponsibilitySequence =
		_.get(payerResponsibilitySequenceMapping, insurance?.sequence)?.display || payerResponsibilitySequenceMapping?.unknown?.display;

	claimMeta.workState = metaData?.workState;
	claimMeta.billedAmount = claimRequest?.total?.value || 0;
	claimMeta.payerName = metaData?.payerName || metaData?.tenantpayerName;
	claimMeta.claimFrequency = metaData?.claimFrequencyCode;
	claimMeta.providerName = metaData?.providerName;
	claimMeta.lastAction = metaData?.lastAction;

	const start = claimRequest?.billablePeriod?.start;
	const end = claimRequest?.billablePeriod?.end;
	claimMeta.submissionPeriod = {
		start: formatDate(start),
		end: formatDate(end),
	};
	return claimMeta;
};

const getFlattenedData = (
	allocationId: string,
	data: ClaimStatusResponse.IClaimWithPatientDetails[],
): {
	key: string;
	accountInfo: {
		accountNumber: string;
		accountAge: number;
	};
	claimInfo: PatientPayment.IClaimInfo;
}[] => {
	return _.map(data, ({ claimId, claimDetails, accountInfo, metaData }) => {
		const isInstitutionalClaim = claimDetails.fhirClaim?.type?.coding?.[0]?.code === CLAIM_TYPES.institutional.value;
		const parsedClaimInfo = getClaimInfo(claimDetails.fhirClaim as Fhir.Claim, isInstitutionalClaim, metaData);

		const financialMeta = getClaimFinancialMeta(claimDetails);

		const claimResponseAvailable = !!claimDetails?.fhirClaimResponse;

		const reconciliations = (claimDetails?.paymentReconciliation || []) as Fhir.PaymentReconciliation[];
		const allocationsDetails: PatientPayment.IAllocationsDetails[] = _.reduce(
			reconciliations,
			(acc: PatientPayment.IAllocationsDetails[], paymentReconciliation: Fhir.PaymentReconciliation) => {
				const allocation = _.find(paymentReconciliation?.allocation, (alloc: Fhir.PaymentReconciliationAllocation) => {
					return alloc?.target?.reference === `Claim/${claimId}` && !!alloc?.amount?.value;
				});

				if (allocation) {
					acc.push({
						amount: allocation?.amount?.value || 0,
						referenceNumber: paymentReconciliation.referenceNumber,
						paymentMethod: getPaymentMethod(paymentReconciliation) || 'Unknown',
						id: paymentReconciliation.id,
					});
				}
				return acc;
			},
			[],
		);

		const collectedPR = {
			total: _.sumBy(allocationsDetails, 'amount'),
			details: allocationsDetails,
		};
		const allocatedAmount = _.find(allocationsDetails, ({ id }) => allocationId === id)?.amount || 0;

		return {
			key: claimId,
			accountInfo: {
				accountNumber: getIdentifierValue(accountInfo?.accountIdentifier, fhirSystemMapping.accountNumber),
				accountAge: accountInfo?.accountAge,
			},
			claimInfo: {
				claimId,
				...parsedClaimInfo,
				...financialMeta,
				billedAmount: financialMeta?.billedAmount || parsedClaimInfo?.billedAmount,
				collectedPR,
				claimResponseAvailable,
				allocatedAmount: allocatedAmount || 0,
				status: claimDetails?.status,
			},
		};
	});
};

type ClaimTableRow = PatientPayment.IClaimInfo & { key: string };

const getParsedClaimWithPatientDetails = (
	data: ClaimStatusResponse.IClaimWithPatientDetails[],
	id: string,
): PatientPayment.IAllocationModel => {
	const flattenedData = getFlattenedData(id, data);
	const groupedData = _.groupBy(flattenedData, (item) => item?.accountInfo?.accountNumber || 'No Account Number');

	const WORKED_STATES = [CLAIM_WORK_STATES.a_adjudicated.value, CLAIM_WORK_STATES.a_payment_posted.value];
	const isWorked = (claim: ClaimTableRow) => _.includes(WORKED_STATES, claim.workState);
	const isUnallocated = (claim: ClaimTableRow) => !claim.allocatedAmount && !claim.pendingPR;
	const byBilledDesc = (claims: ClaimTableRow[]) => _.orderBy(claims, (c) => c.billedAmount || 0, 'desc');

	// Ordering: worked+unallocated -> worked+allocated -> everything else. Each bucket by billed amount desc.
	const sortClaims = (claims: ClaimTableRow[]): ClaimTableRow[] => {
		const [worked, rest] = _.partition(claims, isWorked);
		const [unallocated, allocated] = _.partition(worked, isUnallocated);
		return [...byBilledDesc(unallocated), ...byBilledDesc(allocated), ...byBilledDesc(rest)];
	};

	const hasClaimActivity = (claim: ClaimTableRow) => (claim.pendingPR || 0) + (claim.allocatedAmount || 0) > 0;

	const accountData = _.map(groupedData, (group, accountNumber) => ({
		key: accountNumber,
		accountDetails: group[0]?.accountInfo,
		outstandingPR: _.sumBy(group, ({ claimInfo }) => claimInfo.pendingPR || 0),
		children: sortClaims(_.map(group, ({ claimInfo }) => ({ key: claimInfo.claimId, ...claimInfo }))),
	}));

	// Accounts with any PR activity first, then by age desc, then by outstanding PR desc.
	const sortedAccountData = _.orderBy(
		accountData,
		[
			({ children }) => _.some(children, hasClaimActivity),
			({ accountDetails }) => Number(accountDetails?.accountAge) || 0,
			'outstandingPR',
		],
		['desc', 'desc', 'desc'],
	);
	return {
		id,
		claimsTable: sortedAccountData,
	};
};

const buildAllocationEntry = (
	claim: Fhir.Claim | undefined,
	claimResponse: Fhir.ClaimResponse | undefined,
	allocatedAmount: number,
): Fhir.PaymentReconciliationAllocation => {
	const entry: Fhir.PaymentReconciliationAllocation = {
		amount: { value: allocatedAmount || 0, currency: 'USD' },
	};
	if (claim?.id) {
		entry.target = { reference: `Claim/${claim.id}` };
	}
	if (claimResponse?.id) {
		entry.response = { reference: `ClaimResponse/${claimResponse.id}` };
	}
	return entry;
};

const getPaymentAllocationSubmitModel = ({
	claimData,
	paymentReconciliation,
	editsSnapshot,
	initialEdits,
	isComplete,
}: {
	claimData: ClaimStatusResponse.IClaimWithPatientDetails[];
	paymentReconciliation: Fhir.PaymentReconciliation;
	editsSnapshot: PatientPayment.ClaimEdits;
	initialEdits: PatientPayment.ClaimEdits;
	isComplete: boolean;
}): PatientPayment.IPaymentReconciliationPatchModal => {
	const existingAllocations = _.cloneDeep(paymentReconciliation?.allocation) || [];
	const changedClaimIds = getChangedClaims(editsSnapshot, initialEdits);
	const submitModelAllocations: PatientPayment.IPaymentReconciliationPatchModal['allocations'] = [];

	let nextAllocations = [...existingAllocations];

	_.forEach(changedClaimIds, (claimId) => {
		const resources = _.find(claimData, ({ claimId: id }) => id === claimId);
		const fhirClaim = _.cloneDeep(resources?.claimDetails?.fhirClaim) as Fhir.Claim | undefined;
		const fhirClaimResponse = _.cloneDeep(resources?.claimDetails?.fhirClaimResponse) as Fhir.ClaimResponse | undefined;
		if (!fhirClaim && !fhirClaimResponse) {
			return;
		}

		const edit: PatientPayment.ClaimEdit = editsSnapshot[claimId] || {
			allocatedAmount: 0,
			writeOff: { amount: 0, reasonId: null },
		};
		const initialEdit: PatientPayment.ClaimEdit = initialEdits[claimId] || {
			allocatedAmount: 0,
			writeOff: { amount: 0, reasonId: null },
		};

		const allocatedAmount = _.toNumber(edit.allocatedAmount) || 0;
		const initialAllocatedAmount = _.toNumber(initialEdit.allocatedAmount) || 0;
		const writeOffAmount = _.toNumber(edit.writeOff?.amount) || 0;
		const writeOffReasonId = edit.writeOff?.reasonId ?? null;
		const hasValidWriteOff = writeOffAmount > 0 && !_.isEmpty(writeOffReasonId);
		const writeOffMetadata = hasValidWriteOff ? [{ amount: writeOffAmount, writeOffReasonId }] : [];

		const claimReference = `Claim/${fhirClaim?.id ?? ''}`;
		const existingIndex = _.findIndex(nextAllocations, ({ target }) => target?.reference === claimReference);

		if (allocatedAmount > 0) {
			const allocationEntry = buildAllocationEntry(fhirClaim, fhirClaimResponse, allocatedAmount);
			if (existingIndex >= 0) {
				nextAllocations[existingIndex] = { ...nextAllocations[existingIndex], ...allocationEntry };
			} else {
				nextAllocations.push(allocationEntry);
			}
			submitModelAllocations.push({
				fhirClaim,
				...(fhirClaimResponse ? { fhirClaimResponse } : {}),
				writeOffMetadata,
			});
			return;
		}

		if (initialAllocatedAmount > 0 && existingIndex >= 0) {
			nextAllocations = _.filter(nextAllocations, (_alloc, index) => index !== existingIndex);
		}

		submitModelAllocations.push({
			fhirClaim,
			...(fhirClaimResponse ? { fhirClaimResponse } : {}),
			writeOffMetadata,
		});
	});

	return {
		allocations: submitModelAllocations,
		fhirResource: {
			...paymentReconciliation,
			outcome: isComplete ? 'complete' : 'partial',
			allocation: nextAllocations,
		},
	};
};

const getRefundAllocationsPayload = (
	allocations: PatientPayment.IPaymentReconciliation['allocations'],
): PatientPayment.IPaymentReconciliationPatchModal['allocations'] => {
	return _.map(allocations || [], ({ fhirClaim, fhirClaimResponse, writeOffMetadata }) => ({
		fhirClaim,
		...(fhirClaimResponse ? { fhirClaimResponse } : {}),
		writeOffMetadata: _.isEmpty(writeOffMetadata) ? null : writeOffMetadata,
	}));
};

export { calculatePendingPR, getClaimInfo, getParsedClaimWithPatientDetails, getPaymentAllocationSubmitModel, getRefundAllocationsPayload };
